"use client";

import { useTransition } from "react";
import { useLocale } from "next-intl";
import { usePathname, useRouter } from "@/i18n/navigation";
import { type Locale } from "@/i18n/routing";
import { savePreferredLocale } from "@/i18n/preferred-locale";
import { Check, Globe } from "lucide-react";
import { LANGUAGES, getSelectableLanguages } from "@/i18n/languages";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import "flag-icons/css/flag-icons.min.css";

interface LanguageSwitcherProps {
  variant?: "default" | "white" | "ghost";
  showLabel?: boolean;
  className?: string;
}

export function LanguageSwitcher({
  variant = "default",
  showLabel = true,
  className,
}: LanguageSwitcherProps) {
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const [isPending, startTransition] = useTransition();

  const changeLanguage = (newLocale: string) => {
    if (newLocale === locale) return;

    // Preserve current query parameters — useSearchParams 훅 대신 클릭 시점에
    // window 에서 읽는다. layout(Navbar)에 마운트되는 컴포넌트가 훅을 쓰면 정적 렌더
    // 페이지에서 Suspense 경계까지 CSR bailout 이 생긴다 (LanguageSuggestBanner 와 동일).
    const query: Record<string, string> = {};
    new URLSearchParams(window.location.search).forEach((value, key) => {
      query[key] = value;
    });

    // 명시적 선택이므로 브라우저에 기억 (다음 방문 랜딩 리다이렉트 + 언어 제안 배너 억제)
    savePreferredLocale(newLocale as Locale);

    startTransition(() => {
      // next-intl router handles locale prefix automatically
      // usePathname() returns path WITHOUT locale prefix (e.g., "/products/123")
      const href =
        Object.keys(query).length > 0 ? { pathname, query } : pathname;
      router.replace(href, { locale: newLocale as Locale });
    });
  };

  const currentLanguage = LANGUAGES.find((lang) => lang.code === locale);
  const buttonVariant =
    variant === "white" ? "ghost" : variant === "ghost" ? "ghost" : "outline";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={buttonVariant}
          size="sm"
          disabled={isPending}
          className={cn(
            "h-10 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 rounded-full px-3",
            variant === "white" &&
              "text-white hover:bg-white/10 bg-transparent",
            !showLabel && "w-10 px-0 justify-center",
            className,
          )}
        >
          <Globe className="h-4 w-4" />
          {showLabel && (
            <span className="text-sm font-medium">
              {currentLanguage?.nativeName}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {getSelectableLanguages(locale).map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={cn(
              // 왼쪽 여백 칸에 선택 체크 — 체크 유무와 관계없이 깃발 위치가 같다
              "relative cursor-pointer flex items-center pl-8 pr-4",
              locale === lang.code && "bg-gray-100",
            )}
          >
            {locale === lang.code && (
              <Check className="absolute left-2 h-4 w-4 text-orange-600" />
            )}
            <span className="flex items-center gap-2">
              <span
                className={`fi fi-${lang.flagCode} w-5! h-auto bg-cover! border border-gray-300`}
              />
              <span className="whitespace-nowrap">{lang.nativeName}</span>
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
